import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/store/auth";
import type { AccessModuleKey, AccessPermissionKey } from "@/types/accessControl";

export function useAccessControl() {
  const authStore = useAuthStore();
  const { accessContext, isSuperAdmin } = storeToRefs(authStore);

  const visibleModuleKeys = computed(
    () =>
      new Set(
        (accessContext.value?.module_access?.effective_visible_module_keys || []).map(
          (key) => String(key || "").trim()
        )
      )
  );

  const allowedPermissionKeys = computed(
    () =>
      new Set(
        (accessContext.value?.permission_access?.effective_allowed_permission_keys || []).map(
          (key) => String(key || "").trim()
        )
      )
  );

  const canPermission = (permissionKey: AccessPermissionKey | string): boolean => {
    if (isSuperAdmin.value) {
      return true;
    }

    const normalized = String(permissionKey || "").trim();
    if (!normalized) {
      return false;
    }

    return allowedPermissionKeys.value.has(normalized);
  };

  const canAnyPermission = (
    permissionKeys: Array<AccessPermissionKey | string>
  ): boolean => {
    return permissionKeys.some((permissionKey) => canPermission(permissionKey));
  };

  const canModule = (moduleKey: AccessModuleKey | string): boolean => {
    if (isSuperAdmin.value) {
      return true;
    }

    const normalized = String(moduleKey || "").trim();
    if (!normalized) {
      return false;
    }

    return visibleModuleKeys.value.has(normalized);
  };

  const canModuleAction = (
    moduleKey: AccessModuleKey | string,
    actionKey: string
  ): boolean => {
    const normalizedModule = String(moduleKey || "").trim();
    const normalizedAction = String(actionKey || "").trim();

    if (!normalizedModule || !normalizedAction) {
      return false;
    }

    return canPermission(`${normalizedModule}.${normalizedAction}`);
  };

  return {
    canPermission,
    canAnyPermission,
    canModule,
    canModuleAction,
    accessContext,
  };
}
